import { useMemo, useState } from "react";
import CustomSharedTitleSection from "sipli-fleet-core/components/CustomSharedTitleSection/CustomSharedTitleSection";
import { useQueryGetVehicleTypesList } from "src/queries/administrator/vehicleType.queries";
import type { VehicleTypeRecord } from "src/services/administrator/vehicleTypeService";
import { hasAdministrationPermission } from "src/util/administrationPermission";
import VehicleTypeFormModal from "./VehicleTypeFormModal";
import VehicleTypesTable from "./VehicleTypesTable";
import "../../Customers/Customers.css";

const DEFAULT_PAGINATION = { page: 0, size: 10 };

interface VehicleTypeSorting {
  sortBy: string | undefined;
  sortDir: "ASC" | "DESC" | undefined;
}

const isActive = (value: unknown) => {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") return ["active", "true", "1"].includes(value.toLowerCase());
  return false;
};

const VehicleTypes = () => {
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorting, setSorting] = useState<VehicleTypeSorting>({
    sortBy: "ID",
    sortDir: "DESC",
  });
  const [search, setSearch] = useState("");
  const [formRecord, setFormRecord] = useState<VehicleTypeRecord | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const canEdit = hasAdministrationPermission("edit");

  const queryParams = useMemo(
    () => ({
      page: pagination.page,
      size: pagination.size,
      sort: sorting.sortDir ?? "DESC",
      sortBy: sorting.sortBy ?? "ID",
    }),
    [pagination.page, pagination.size, sorting.sortBy, sorting.sortDir],
  );

  const { data, isLoading } = useQueryGetVehicleTypesList(queryParams, { enabled: true });
  const response = data?.data?.response;
  const records = useMemo(
    () =>
      response?.vehicleTypes ??
      response?.data ??
      response?.items ??
      response?.content ??
      [],
    [response],
  );

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();
    if (!normalizedSearch) return records;

    return records.filter((record) =>
      [record.name, record.description, isActive(record.active) ? "active" : "inactive"]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(normalizedSearch),
    );
  }, [records, search]);

  const totalCount = search.trim()
    ? filteredRecords.length
    : (response?.totalItems ?? response?.totalElements ?? response?.total ?? records.length);

  return (
    <section className="pb-8 pl-2 bg-gray1">
      <CustomSharedTitleSection
        titleKey="vehicle_type"
        buttonKey="create"
        onClick={() => {
          setFormRecord(null);
          setIsFormOpen(true);
        }}
        showBtn={canEdit}
        disabled
        buttonProps={{ className: "!text-gray2 !w-[94px] !h-[40px] !opacity-50 !cursor-not-allowed" }}
        titleClassName="!text-[25.23px] !font-[700]"
      />

      <div className="flex flex-col overflow-hidden bg-white rounded-[16px]">
        <VehicleTypesTable
          data={filteredRecords}
          isLoading={isLoading}
          pagination={{ ...pagination, total: totalCount }}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          search={search}
          setSearch={setSearch}
          onEdit={(record) => {
            setFormRecord(record);
            setIsFormOpen(true);
          }}
        />
      </div>

      <VehicleTypeFormModal
        open={isFormOpen}
        record={formRecord}
        onClose={() => setIsFormOpen(false)}
      />
    </section>
  );
};

export default VehicleTypes;
